import { useEffect, useMemo, useState } from 'react';
import { useToast } from '@/contexts/ToastContext';
import type { PrintInvoiceData } from '@/utils/printDocument';
import {
  buildThermalHtml,
  buildUpiUrl,
  getCompanyInfo,
  getPrintSettings,
  isValidUpiId,
  makeQrDataUrl,
  CompanyInfo,
  ThermalWidth,
} from '@/utils/printThermalInvoice';
import {
  DEFAULT_THERMAL_DESIGN,
  THERMAL_FIELDS,
  getThermalDesign,
  normalizeThermalDesign,
  saveThermalDesign,
  ThermalDesign,
  ThermalField,
  ThermalFieldKey,
} from '@/utils/thermalDesign';

const SAMPLE: PrintInvoiceData = {
  invoiceNo: 'NGL-INV-2026-0001',
  date: '02/11/2026',
  customerName: 'Rajan',
  customerGstin: '',
  billingAddress: 'Rohini, Delhi',
  paymentMode: 'Cash',
  paymentStatus: 'PAID',
  paidAmount: 1086,
  balanceDue: 0,
  isSameState: true,
  subtotal: 1034.29,
  totalDiscount: 0,
  taxableAmount: 1034.29,
  cgst: 25.86,
  sgst: 25.86,
  igst: 0,
  roundOff: -0.01,
  grandTotal: 1086,
  paymentModes: [{ mode: 'Cash', amount: 1086 }],
  items: [
    { sr: 1, name: 'Malai Chaap (Half)', hsn: '9963', qty: 1, unit: 'Pcs', rate: 140, discount: 0, taxable: 140, taxRate: 5, taxAmt: 7, total: 147 },
    { sr: 2, name: 'Butter Roti', hsn: '9998', qty: 4, unit: 'Pcs', rate: 90, discount: 0, taxable: 360, taxRate: 5, taxAmt: 18, total: 378 },
    { sr: 3, name: '750ML Cold Drink', hsn: '2202', qty: 1, unit: 'Box', rate: 370, discount: 0, taxable: 370, taxRate: 0, taxAmt: 0, total: 370 },
  ],
} as unknown as PrintInvoiceData;

const LINE_OPTIONS = [
  { v: 1.2, label: 'Compact' },
  { v: 1.3, label: 'Normal' },
  { v: 1.5, label: 'Wide' },
];

const PLACEHOLDER_COMPANY: CompanyInfo = {
  name: 'Your Company Name',
  address: 'Shop address, City',
  gstin: '07ABCDE1234F1Z5',
  phone: '9999999999',
  email: '',
  logo: '',
};

export default function ThermalDesignCard() {
  const toast = useToast();
  const [design, setDesign] = useState<ThermalDesign>(getThermalDesign);
  const [printCfg] = useState(getPrintSettings);
  const [previewWidth, setPreviewWidth] = useState<ThermalWidth>(
    printCfg.format === 'THERMAL_58' ? 58 : printCfg.format === 'THERMAL_CUSTOM' ? printCfg.thermalWidthMm : 80,
  );
  const [company, setCompany] = useState<CompanyInfo>(PLACEHOLDER_COMPANY);
  const [qrData, setQrData] = useState('');
  const [frameH, setFrameH] = useState(600);

  useEffect(() => {
    getCompanyInfo().then((c) => setCompany(c)).catch(() => {});
  }, []);

  useEffect(() => {
    if (printCfg.qrMode === 'upi' && isValidUpiId(printCfg.upiId)) {
      makeQrDataUrl(buildUpiUrl(printCfg.upiId, printCfg.upiName || 'Merchant', 1086)).then(setQrData);
    } else if (printCfg.qrMode === 'image' && printCfg.qrImage) {
      setQrData(printCfg.qrImage);
    }
  }, [printCfg]);

  const html = useMemo(
    () =>
      buildThermalHtml(SAMPLE, {
        width: previewWidth,
        company,
        settings: printCfg,
        design: normalizeThermalDesign(design),
        qrDataUrl: qrData,
        autoPrint: false,
        terms: printCfg.terms.length ? printCfg.terms : ['Goods once sold will not be taken back.'],
      }),
    [previewWidth, company, printCfg, design, qrData],
  );

  const setField = (key: ThermalFieldKey, prop: keyof ThermalField, val: boolean | number) => {
    setDesign((p) => ({ ...p, fields: { ...p.fields, [key]: { ...p.fields[key], [prop]: val } } }));
  };

  const set = <K extends keyof ThermalDesign>(key: K, val: ThermalDesign[K]) => setDesign((p) => ({ ...p, [key]: val }));

  const handleSave = () => {
    const clean = normalizeThermalDesign(design);
    if (saveThermalDesign(clean)) {
      setDesign(clean);
      toast.success('Thermal bill design saved for this computer');
    } else {
      toast.error('Save nahi hua');
    }
  };

  const handleReset = () => {
    setDesign(normalizeThermalDesign(DEFAULT_THERMAL_DESIGN));
    toast.info?.('Default design loaded — Save dabao to apply');
  };

  const labelCls = 'block text-xs font-semibold text-[#64748b] mb-1.5 uppercase tracking-wide';
  const numCls =
    'w-16 h-7 border border-[#e2e8f0] rounded-md px-1.5 text-xs text-right outline-none focus:border-[#4f46e5]';
  const btn = (active: boolean) =>
    `h-8 px-3 rounded-md border text-xs font-medium cursor-pointer transition-colors ${
      active ? 'bg-[#4f46e5] border-[#4f46e5] text-white' : 'bg-white border-[#e2e8f0] text-[#64748b] hover:border-[#4f46e5]/40'
    }`;
  const numInput = (value: number, onChange: (v: number) => void, min: number, max: number, step = 0.5) => (
    <input
      type="number"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => {
        const v = parseFloat(e.target.value);
        if (!Number.isNaN(v)) onChange(v);
      }}
      className={numCls}
    />
  );

  return (
    <div className="bg-white border border-[#e2e8f0] rounded-xl p-6">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <i className="ri-receipt-line text-[#4f46e5] text-base" />
          <h3 className="text-sm font-semibold text-[#1e293b]">Thermal Bill Design</h3>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleReset}
            className="h-8 px-3 rounded-lg border border-[#e2e8f0] text-xs font-medium text-[#64748b] hover:bg-slate-50 cursor-pointer"
          >
            <i className="ri-refresh-line" /> Reset
          </button>
          <button
            onClick={handleSave}
            className="flex items-center gap-2 h-8 px-4 rounded-lg bg-[#4f46e5] text-white text-xs font-medium hover:bg-indigo-700 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-save-line" /> Save
          </button>
        </div>
      </div>
      <p className="text-xs text-[#64748b] mb-5">
        Har hisse ka font size (px), Bold aur show/hide set karo — right side mein bill ka live preview dikhega.
      </p>

      <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-6 items-start">
        {/* LEFT — settings */}
        <div className="space-y-5 min-w-0">
          {/* Fields */}
          <div>
            <label className={labelCls}>Fields &amp; Font Size</label>
            <div className="border border-[#e2e8f0] rounded-lg divide-y divide-[#f1f5f9]">
              {THERMAL_FIELDS.map(({ key, label, canHide, sizeable }) => {
                const fld = design.fields[key];
                const visible = canHide ? fld.show : true;
                return (
                  <div key={key} className="flex items-center justify-between gap-3 px-3 h-10">
                    <label className={`flex items-center gap-2 min-w-0 ${canHide ? 'cursor-pointer' : ''}`}>
                      {canHide ? (
                        <input
                          type="checkbox"
                          checked={fld.show}
                          onChange={(e) => setField(key, 'show', e.target.checked)}
                          className="w-3.5 h-3.5 accent-[#4f46e5] cursor-pointer shrink-0"
                        />
                      ) : (
                        <i className="ri-lock-line text-[#cbd5e1] text-xs w-3.5 shrink-0" title="Hamesha dikhega" />
                      )}
                      <span className={`text-xs truncate ${visible ? 'text-[#1e293b]' : 'text-[#94a3b8] line-through'}`}>
                        {label}
                      </span>
                    </label>
                    {sizeable && visible && (
                      <div className="flex items-center gap-1.5 shrink-0">
                        {numInput(fld.size, (v) => setField(key, 'size', v), 6, 40)}
                        <span className="text-[10px] text-[#94a3b8] w-4">px</span>
                        <button
                          type="button"
                          onClick={() => setField(key, 'bold', !fld.bold)}
                          title="Bold"
                          className={`w-7 h-7 rounded-md border text-xs font-bold cursor-pointer ${
                            fld.bold ? 'bg-[#4f46e5] border-[#4f46e5] text-white' : 'bg-white border-[#e2e8f0] text-[#64748b]'
                          }`}
                        >
                          B
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Title + spacing */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Bill Title</label>
              <input
                type="text"
                value={design.titleText}
                placeholder="TAX INVOICE"
                onChange={(e) => set('titleText', e.target.value)}
                className="w-full h-9 px-3 text-sm border border-[#e2e8f0] rounded-lg outline-none focus:border-[#4f46e5]"
              />
            </div>
            <div>
              <label className={labelCls}>Line Spacing</label>
              <div className="flex gap-1.5">
                {LINE_OPTIONS.map((o) => (
                  <button key={o.v} type="button" onClick={() => set('lineHeight', o.v)} className={btn(design.lineHeight === o.v)}>
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Sizes */}
          <div className="grid grid-cols-4 gap-4">
            <div>
              <label className={labelCls}>Side Margin</label>
              <div className="flex items-center gap-1.5">
                {numInput(design.sideMarginMm, (v) => set('sideMarginMm', v), 0, 10)}
                <span className="text-[11px] text-[#94a3b8]">mm</span>
              </div>
            </div>
            <div>
              <label className={labelCls}>Logo Height</label>
              <div className="flex items-center gap-1.5">
                {numInput(design.logoHeightMm, (v) => set('logoHeightMm', v), 5, 30)}
                <span className="text-[11px] text-[#94a3b8]">mm</span>
              </div>
            </div>
            <div>
              <label className={labelCls}>QR Size</label>
              <div className="flex items-center gap-1.5">
                {numInput(design.qrSizeMm, (v) => set('qrSizeMm', v), 15, 50)}
                <span className="text-[11px] text-[#94a3b8]">mm</span>
              </div>
            </div>
            <div>
              <label className={labelCls}>Small Roll Font</label>
              <div className="flex items-center gap-1.5">
                {numInput(design.scale58, (v) => set('scale58', v), 60, 110, 5)}
                <span className="text-[11px] text-[#94a3b8]">%</span>
              </div>
            </div>
          </div>

          <p className="text-xs text-[#94a3b8]">
            Tip: 80mm roll ke liye normal text 10–12px, Grand Total 14–18px theek rehta hai. Zyada bada size doge toh
            item ka naam do line mein chala jayega.
          </p>
        </div>

        {/* RIGHT — live preview */}
        <div className="sticky top-4">
          <div className="flex items-center justify-between mb-2">
            <label className={`${labelCls} mb-0`}>Live Preview</label>
            <div className="flex gap-1">
              <button type="button" onClick={() => setPreviewWidth(80)} className={btn(previewWidth === 80)}>
                80mm
              </button>
              <button type="button" onClick={() => setPreviewWidth(58)} className={btn(previewWidth === 58)}>
                58mm
              </button>
              {printCfg.thermalWidthMm !== 80 && printCfg.thermalWidthMm !== 58 && (
                <button
                  type="button"
                  onClick={() => setPreviewWidth(printCfg.thermalWidthMm)}
                  className={btn(previewWidth === printCfg.thermalWidthMm)}
                >
                  {printCfg.thermalWidthMm}mm
                </button>
              )}
            </div>
          </div>
          <div className="bg-[#e5e7eb] rounded-lg overflow-auto max-h-[720px]">
            <iframe
              title="Thermal preview"
              srcDoc={html}
              onLoad={(e) => {
                const doc = e.currentTarget.contentDocument;
                if (doc) setFrameH(doc.documentElement.scrollHeight + 10);
              }}
              style={{ width: Math.round((previewWidth * 96) / 25.4) + 40, height: frameH, border: 0, display: 'block' }}
            />
          </div>
          <p className="text-[11px] text-[#94a3b8] mt-1.5">Sample data · QR aur T&amp;C Print Settings se aate hain</p>
        </div>
      </div>
    </div>
  );
}
